import { TypePolicies } from "@apollo/client";

import {
  Maybe,
  ModuleRepositoryMetadata,
  SpaceliftMetrics,
  StackIntegrations,
  TerraformProviderDocs,
  UserGuideMetadata,
  UserGuideProgress,
  Version,
} from "types/generated";

import { infiniteScrollArrayFieldPolicy, infiniteScrollFieldPolicy } from "./helpers";

const TYPE_POLICIES: TypePolicies = {
  Query: {
    fields: {
      searchVCSIntegrations: {
        keyArgs: ["input"],
      },
      availableAIModels: {
        keyArgs: ["provider", "apiKey", "baseURL", "space"],
      },
      metrics: {
        merge(existing: Maybe<SpaceliftMetrics>, incoming: Maybe<SpaceliftMetrics>) {
          return { ...existing, ...incoming };
        },
      },
      metricsRange: {
        merge(existing: Maybe<SpaceliftMetrics>, incoming: Maybe<SpaceliftMetrics>) {
          return { ...existing, ...incoming };
        },
        keyArgs: false,
      },
      activeUserGuideProgress: {
        merge(existing: Maybe<UserGuideProgress>, incoming: Maybe<UserGuideProgress>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
      completedUserGuides: {
        merge: false,
      },
    },
  },
  AuditTrailWebhook: {
    keyFields: ["endpoint"],
  },
  ConfigElement: {
    keyFields: ["id", "type"],
  },
  Stack: {
    fields: {
      integrations: {
        merge(existing: StackIntegrations, incoming: StackIntegrations) {
          return { ...existing, ...incoming };
        },
      },
      runs: infiniteScrollArrayFieldPolicy(),
      tasks: infiniteScrollArrayFieldPolicy(),
    },
  },
  Module: {
    fields: {
      versions: {
        keyArgs: ["includeFailed"],
        merge(existing: Map<string, Version>, incoming: Version[], { readField }) {
          const merged = new Map<string, Version>(existing);

          for (const item of incoming) {
            const id = readField<string>("id", item);

            if (id) {
              merged.set(id, item);
            }
          }

          return merged;
        },
        read(existing: Map<string, Version>) {
          return existing && Array.from(existing.values());
        },
      },
    },
  },
  Version: {
    fields: {
      metadata: {
        merge(existing: ModuleRepositoryMetadata, incoming: ModuleRepositoryMetadata) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  Repository: {
    keyFields: ["name", "namespace"],
  },
  BlueprintInput: {
    keyFields: false,
  },
  TemplateDeploymentInput: {
    keyFields: false,
  },
  TemplateDeployment: {
    fields: {
      inputs: {
        merge: false,
      },
    },
  },
  VCSIntegration: {
    keyFields: ["id", "provider"],
  },
  // id is the provider's PR number (unique only per stack/module), so the parent owns it.
  PullRequest: {
    keyFields: false,
  },
  SpaceliftMetrics: {
    keyFields: false,
  },
  Space: {
    keyFields: false,
  },
  UserGuide: {
    fields: {
      metadata: {
        merge(existing: UserGuideMetadata, incoming: UserGuideMetadata) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  WorkerPool: {
    fields: {
      searchUsers: infiniteScrollFieldPolicy(),
      searchWorkers: infiniteScrollFieldPolicy(),
      searchSchedulableRuns: infiniteScrollFieldPolicy(),
    },
  },
  PublicWorkerPool: {
    fields: {
      searchUsers: infiniteScrollFieldPolicy(),
      searchSchedulableRuns: infiniteScrollFieldPolicy(),
    },
  },
  StackOutput: {
    keyFields: false,
  },
  TerraformProviderVersion: {
    fields: {
      docs: {
        merge(existing: Maybe<TerraformProviderDocs>, incoming: Maybe<TerraformProviderDocs>) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
};

export default TYPE_POLICIES;
